import { CancelToken } from 'axios';
import { AxiosClient } from './AxiosClient';
import { IRequestOptions } from './Interfaces';

export const projectEndPoints = {
    getProjects: async (teamId) => {
        return await AxiosClient.instance.get(
            `teams/projects/?team_id=${teamId}`
        );
    },
    createProject: async (body) => {
        return await AxiosClient.instance.post('/teams/project/', body);
    },
    editProject: async (body) => {
        return await AxiosClient.instance.patch(`/projects/`, body);
    },
    updateProjectInfo: async (
        projectId: number,
        body: { project_info: object }
    ) => {
        return await AxiosClient.instance.patch(
            `/teams/project/document/${projectId}/`,
            body
        );
    },
};

export const departmentEndPoints = {
    getDepartments: async (username, password) => {
        return await AxiosClient.instance.get('/office/departments/', null, {
            headers: {
                'x-ike-username': username,
                'x-ike-password': password,
            },
        });
    },
    linkDeptartment: async (body) => {
        return await AxiosClient.instance.post(
            '/departments/link-department/',
            body
        );
    },
};

export const processEndPoints = {
    getProcesses: async () => {
        return await AxiosClient.instance.get('/processlibrary/');
    },
    runProcess: async (fileData) => {
        return await AxiosClient.instance.post('/processrun/', fileData);
    },

    getProcessProgress: async (taskId) => {
        return await AxiosClient.instance.get(`/celery-progress/${taskId}/`);
    },

    cancelProcess: async (taskId) => {
        return await AxiosClient.instance.post(
            `/celery-progress/${taskId}/cancel/`
        );
    },

    getUserProcessesByProject: async (userId, projectId) => {
        const params = {
            user_id: userId,
            project_id: projectId,
        };
        return await AxiosClient.instance.get(
            '/users/get-historical-tasks-by-user/',
            params
        );
    },
};

export const folderEndPoints = {
    getFolders: async (projectId) => {
        const params = {
            project_id: projectId,
        };
        return await AxiosClient.instance.get('/projects/get-folders/', params);
    },

    getFolderFields: async (folderId) => {
        return await AxiosClient.instance.get(
            `/folders/get-all-fields-folder/${folderId}/`
        );
    },

    createFolder: async (wpData) => {
        return await AxiosClient.instance.post('/folders/', wpData);
    },

    editFolder: async (wpData, folder_id) => {
        return await AxiosClient.instance.patch(
            `/folders/${folder_id}/`,
            wpData
        );
    },

    createFolderSubset: async (folderId, amount) => {
        return await AxiosClient.instance.post(
            `/entityversion/create-sample-data/?folderId=${folderId}&sampleNumber=${amount}`
        );
    },
};

export const entityEndPoints = {
    getDetailedEntityData: async (entityId) => {
        return await AxiosClient.instance.get(`/entities/${entityId}/detailed`);
    },

    getEntityVersionById: async (entityVersionId, options?) => {
        return await AxiosClient.instance.get(
            `/entityversion/${entityVersionId}/`,
            null,
            options
        );
    },

    getEntityImage: async (imageId, isThumbnail, requestOptions?) => {
        const imageType = isThumbnail ? 'thumbnail' : 'image';

        const options: IRequestOptions = {
            ...requestOptions,
            responseType: 'blob',
        };

        return await AxiosClient.instance.get(
            `/images/${imageId}/${imageType}/`,
            null,
            options
        );
    },

    getEntityImageDetails: async (imageId: string, options?) => {
        return await AxiosClient.instance.get(
            `/images/${imageId}/`,
            null,
            options
        );
    },
};

export const organizationEndPoints = {
    inviteUserToOrganization: async (body) => {
        return await AxiosClient.instance.post(`/inviteUser/`, body);
    },
    getOrganizations: async (includeEmail?) => {
        return await AxiosClient.instance.get(
            `/organisations/${includeEmail ? '?email=true' : ''}`
        );
    },
    createOrganization: async (body) => {
        return await AxiosClient.instance.post('/organisations/', body);
    },
    editOrganization: async (body) => {
        return await AxiosClient.instance.patch(`/organisations/`, body);
    },
    getOrganizationUsers: async (organization_id) => {
        return await AxiosClient.instance.get(
            `/organisations/users/?organisation_id=${organization_id}`
        );
    },
    createOrganizationMember: async (body) => {
        return await AxiosClient.instance.post(`/organisations/user/`, body);
    },
    deleteOrganizationMember: async (orgId, email) => {
        return await AxiosClient.instance.delete(
            `/organisations/delete/user/?organisation_id=${orgId}&user_email=${email}`
        );
    },
};

export const teamEndPoints = {
    getAllTeams: async () => {
        return await AxiosClient.instance.get('/teams/');
    },
    getOrganizationTeams: async (organizationId) => {
        return await AxiosClient.instance.get(
            `/teams/?organisationId=${organizationId}`
        );
    },
    createTeam: async (body) => {
        return await AxiosClient.instance.post('/teams/', body);
    },
    updateTeam: async (body) => {
        return await AxiosClient.instance.patch(`/teams/`, body);
    },
    getTeamUsers: async (teamId) => {
        return await AxiosClient.instance.get(
            `/teams/users/?team_id=${teamId}`
        );
    },
    getTeamUsersInDepartment: async (teamId, departmentId) => {
        return await AxiosClient.instance.get(
            `/teams/users/department/?team_id=${teamId}&department_id=${departmentId}`
        );
    },
    createTeamUsers: async (body) => {
        return await AxiosClient.instance.post(`/teams/user/`, body);
    },
    deleteTeamUser: async (teamId, userId) => {
        return await AxiosClient.instance.delete(
            `/teams/delete/user/?team_id=${teamId}&user_id=${userId}`
        );
    },
};

export const fileEndPoints = {
    uploadFile: async (fileData) => {
        return await AxiosClient.instance.post('/fileuploads/', fileData);
    },

    createEntitiesFromFile: async (fileData) => {
        return await AxiosClient.instance.post(
            '/fileuploads/create-entities-bulk-from-file/',
            fileData
        );
    },

    getProjectFiles: async (projectId) => {
        return await AxiosClient.instance.get(
            `/raw/get-all-raw-with-project-id/${projectId}`
        );
    },

    downloadProjectFile: async (folderId) => {
        // todo: use this end point once we have sorted auth
        // return await AxiosClient.instance.get(`/geoserver/koru/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=koru%3AdownloadDatabyFolder&VIEWPARAMS=folder_id:${folderId}&outputFormat=csv`, null)
        return await AxiosClient.instance.get(
            `/geoserver/koru/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=koru%3AdownloadDatabyWorkpacket&VIEWPARAMS=workpacket_id:${folderId}&outputFormat=csv`,
            null
        );
    },

    uploadSingleFileToFolder: async (body) => {
        return await AxiosClient.instance.post(
            '/images/upload-image-create-entity/',
            body
        );
    },

    downloadFileFromUrl: async (url) => {
        const options: IRequestOptions = {
            responseType: 'blob',
        };
        return await AxiosClient.instance.get(url, null, options);
    },
};

export const qualityControlEndpoints = {
    uploadQcResults: async (body) => {
        return await AxiosClient.instance.post(
            '/qcinspection/add-qc-inspection-detail/',
            body
        );
    },

    getQcResults: async (folderId) => {
        return await AxiosClient.instance.get(
            `/qcinspection/get-qc-inspection-detail/?folder_id=${folderId}`
        );
    },
    getSpans: async (entityId, projectId, cancelToken: CancelToken) => {
        return await AxiosClient.instance.get(
            `/spans/get-spans-by-entity-version-v2/?entity_version=${entityId}&project_id=${projectId}`,
            null,
            { cancelToken }
        );
    },

    getImageById: async (imageId) => {
        return await AxiosClient.instance.get(`/images/${imageId}/image/`, {
            responseType: 'blob',
        });
    },

    getInspectionDetails: async (folderId) => {
        return await AxiosClient.instance.get(
            `qcinspection/get-qc-inspection-detail/?folder_id=${folderId}`
        );
    },

    getReworkStageTopicReason: async () => {
        return await AxiosClient.instance.get(
            'qcinspection/get-qc-rework-stage-topic-reason/'
        );
    },

    getQcStatuses: async (entityVersionId) => {
        return await AxiosClient.instance.get(
            `/qcinspection/get-qc-status/?entity_version_id=${entityVersionId}`
        );
    },
};

export const userEndPoints = {
    getUsers: async () => {
        return await AxiosClient.instance.get('/users/');
    },
};

export const adminEndPoints = {
    getIsUserSuperAdmin: async () => {
        return await AxiosClient.instance.get('/superadmins/check-superadmin/');
    },

    getAllSuperAdmins: async () => {
        return await AxiosClient.instance.get('/superadmins/');
    },

    createSuperAdmin: async (content) => {
        return await AxiosClient.instance.post(
            `/superadmins/?email=${content}`
        );
    },

    removeSuperAdmin: async (content) => {
        return await AxiosClient.instance.delete(
            `/superadmins/?email_address=${content}`
        );
    },
};

export const iiqEndPoints = {
    getAutomationTasks: async () => {
        return await AxiosClient.instance.get('/iiq/automation_tasks/');
    },
};

export const autoQcEndPoints = {
    getAutoQcMasterCatalogue: async () => {
        return await AxiosClient.instance.get(
            '/qcinspection/auto-qc-master-catalogue/'
        );
    },

    getProjectAutoQcConfig: async (projectId: number) => {
        return await AxiosClient.instance.get(
            `/autoqcconfig/get-config-by-project/?projectId=${projectId}`
        );
    },

    updateProjectAutoQcConfig: async (projectId: number, config: object) => {
        const body = {
            project_fk: projectId,
            config: config,
        };

        return await AxiosClient.instance.post(`/autoqcconfig/`, body);
    },
};
